/**
 * Escritor de PDF no browser, sem dependência nova: uma página, uma imagem.
 *
 * É o par do `zip.ts` para as placas preenchidas — elas só existem aqui,
 * rasterizadas do DOM, e a gráfica pede PDF. O formato é o mínimo que qualquer
 * leitor abre: catálogo, árvore de páginas, a página, a imagem e o fluxo que a
 * desenha, com a tabela de referências cruzadas no fim.
 *
 * A imagem entra SEM perda (FlateDecode sobre os pixels crus), e não como JPEG:
 * compressão com perda borra a aresta dos módulos do QR e o contorno do código,
 * que é justamente o que precisa sair nítido impresso.
 */

export interface ImagePdfOptions {
  /** Os pixels, como o canvas entrega. O alfa é descartado: a captura já vem com fundo branco. */
  pixels: ImageData;
  /** A folha, em pontos (1/72 pol). */
  page: { w: number; h: number };
  /** Largura impressa da imagem, em pontos. A altura segue a proporção dos pixels. */
  width: number;
}

/* `CompressionStream('deflate')` é zlib (RFC 1950), que é exatamente o que o
   FlateDecode do PDF espera — o `deflate-raw` sem cabeçalho não abriria. */
const deflate = async (bytes: Uint8Array<ArrayBuffer>) => {
  const stream = new Blob([bytes]).stream().pipeThrough(new CompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
};

/** Número de PDF: sem notação exponencial e sem casas a mais. */
const num = (value: number) => String(Math.round(value * 1000) / 1000);

/** Uma página do tamanho de `page` com a imagem centralizada nela. */
export const createImagePdf = async ({ pixels, page, width }: ImagePdfOptions): Promise<Blob> => {
  const { width: columns, height: rows, data } = pixels;
  const rgb = new Uint8Array(columns * rows * 3);
  for (let from = 0, to = 0; from < data.length; from += 4, to += 3) {
    rgb[to] = data[from];
    rgb[to + 1] = data[from + 1];
    rgb[to + 2] = data[from + 2];
  }
  const image = await deflate(rgb);

  const height = width * rows / columns;
  const x = (page.w - width) / 2;
  const y = (page.h - height) / 2;
  const encoder = new TextEncoder();
  const content = encoder.encode(`q ${num(width)} 0 0 ${num(height)} ${num(x)} ${num(y)} cm /Placa Do Q`);

  const parts: Uint8Array<ArrayBuffer>[] = [];
  const offsets: number[] = [];
  let length = 0;
  const write = (chunk: string | Uint8Array<ArrayBuffer>) => {
    const bytes = typeof chunk === 'string' ? encoder.encode(chunk) : chunk;
    parts.push(bytes);
    length += bytes.length;
  };
  const object = (dictionary: string, stream?: Uint8Array<ArrayBuffer>) => {
    offsets.push(length);
    write(`${offsets.length} 0 obj\n`);
    if (!stream) {
      write(`${dictionary}\nendobj\n`);
      return;
    }
    write(`${dictionary.replace(/>>$/, `/Length ${stream.length} >>`)}\nstream\n`);
    write(stream);
    write('\nendstream\nendobj\n');
  };

  // A segunda linha com bytes acima de 127 avisa às ferramentas que o arquivo
  // é binário — sem ela, transferência em modo texto pode corromper a imagem.
  write('%PDF-1.4\n%âãÏÓ\n');
  object('<< /Type /Catalog /Pages 2 0 R >>');
  object('<< /Type /Pages /Kids [3 0 R] /Count 1 >>');
  object(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${num(page.w)} ${num(page.h)}] `
    + '/Resources << /XObject << /Placa 4 0 R >> >> /Contents 5 0 R >>');
  object(`<< /Type /XObject /Subtype /Image /Width ${columns} /Height ${rows} `
    + '/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /FlateDecode >>', image);
  object('<< >>', content);

  // Cada entrada da tabela tem exatamente 20 bytes, o fim de linha incluso —
  // é por isso que o espaço antes do `\n` não pode sair.
  const xref = length;
  write(`xref\n0 ${offsets.length + 1}\n0000000000 65535 f \n`);
  offsets.forEach(offset => write(`${String(offset).padStart(10, '0')} 00000 n \n`));
  write(`trailer\n<< /Size ${offsets.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);

  return new Blob(parts, { type: 'application/pdf' });
};
